import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { DataService } from 'src/app/main/services/data.service';
import { HTTPService } from 'src/app/main/services/HTTPService';
import CONFIG from 'src/app/main/urls/urls';

@Component({
  selector: 'app-check-in',
  templateUrl: './check-in.component.html',
  styleUrls: ['./check-in.component.css']
})
export class CheckInComponent implements OnInit {

  circulationForm: FormGroup;
  loading = false;
  submitted = false;
  members$: any[] = [];
  books$: any[] = [];
  returnStatus$: any[] = [];
  writers$: any[] = [];

  constructor(
    private toastr: ToastrService,
    private dataService: DataService,
    private fb: FormBuilder,
    private httpService: HTTPService,
    private activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    this.circulationForm = this.fb.group({
      memberName: ['', Validators.required],
      // Array of selected book IDs
      catalogItemNames: [[], Validators.required],
      returnDueDate: ['', Validators.required],
      rememberReturnDate: [false]
    });

    this.getAll();
  }

  getAll() {
    this.loading = true;

    forkJoin({
      books: this.httpService.getAll(CONFIG.URL_BASE + '/book/all'),
      members: this.httpService.getAll(CONFIG.URL_BASE + '/member/all'),
      returnStatus: this.httpService.getAll(CONFIG.URL_BASE + '/circulationstatus/all'),
      writers: this.httpService.getAll(CONFIG.URL_BASE + '/writer/all')
    }).subscribe(
      ({ books, members, returnStatus, writers }) => {
        this.books$ = books as any[];
        this.members$ = members as any[];
        this.returnStatus$ = returnStatus as any[];
        this.writers$ = writers as any[];
        this.loading = false;
      },
      (err: HttpErrorResponse) => {
        this.loading = false;
        console.error('Error fetching data:', err.message);
      }
    );
  }

  add() {
    this.submitted = true;

    if (!this.validateCirculationForm()) {
      return;
    }

    const formValue = this.circulationForm.value;
    const selectedMember = this.members$.find(x => x.id === parseInt(formValue.memberName));
    const selectedReturnStatus = this.returnStatus$.find(x => x.name === 'CheckIn');

    // Resolve each selected book ID to the full book object
    const selectedBookIds: number[] = formValue.catalogItemNames.map((id: any) => parseInt(id));
    const selectedBooks = this.books$.filter(x => selectedBookIds.includes(x.id));

    if (selectedBooks.length === 0) {
      this.toastr.error('No valid books selected.');
      return;
    }

    // Create one circulation record per selected book
    const requests = selectedBooks.map(book => {
      const body = {
        memberName: selectedMember,
        catalogItemName: book,
        issueDate: new Date().toDateString(),
        returnStatus: selectedReturnStatus,
        returnDate: formValue.returnDueDate,
        toReturn: formValue.returnDueDate
      };
      return this.httpService.create(CONFIG.URL_BASE + '/circulation/create', body);
    });

    Promise.all(requests).then(() => {
      this.dataService.triggerRefresh();
      this.toastr.success(`${selectedBooks.length} item(s) checked in successfully.`);
      selectedBooks.forEach(book =>
        this.addNotification(selectedMember.firstname, book.title)
      );
      this.closeModal();
    }).catch(err => {
      console.error('Check-in error:', err);
      this.toastr.error('An error occurred during check-in.');
    });
  }

  addNotification(name: string, title: string): void {
    const newNotification = {
      content: `${name} has checked in ${title}`,
      date: new Date(),
      type: 'info'
    };

    this.httpService.create(CONFIG.URL_BASE + '/notification/', newNotification).then(
      notification => console.log('Notification created:', notification),
      error => console.error('Error creating notification', error)
    );
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  validateCirculationForm(): boolean {
    if (!this.circulationForm) {
      this.toastr.error('Form is not initialized.');
      return false;
    }

    const formValue = this.circulationForm.value;

    if (!formValue.memberName) {
      this.toastr.error('Please select a member.');
      return false;
    }

    if (!formValue.catalogItemNames || formValue.catalogItemNames.length === 0) {
      this.toastr.error('Please select at least one item.');
      return false;
    }

    if (!formValue.returnDueDate) {
      this.toastr.error('Please select a return due date.');
      return false;
    }

    const dueDate = new Date(formValue.returnDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dueDate < today) {
      this.toastr.error('Return due date cannot be in the past.');
      return false;
    }

    return true;
  }
}